import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  UserCircleIcon,
  PencilIcon,
  BuildingOfficeIcon,
  ShieldCheckIcon,
  ClockIcon,
  KeyIcon,
  CheckCircleIcon,
  XCircleIcon,
  CameraIcon,
  LockClosedIcon,
  BellIcon,
  GlobeAltIcon,
  SunIcon,
  MoonIcon,
  ComputerDesktopIcon,
  ExclamationTriangleIcon,
  Cog6ToothIcon,
  Cog6ToothIcon as PaletteIcon
} from '@heroicons/react/24/outline'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import PhoneNumberInput from '@/components/ui/PhoneNumberInput'
import TwoFactorSetup from '@/components/ui/TwoFactorSetup'
import { useAuth } from '@/hooks/useAuth'
import { updateUser } from '@/api/usersApi'
import { institutionsApi } from '@/api/institutionsApi'
import toast from 'react-hot-toast'

// Form schemas
const editProfileSchema = z.object({
  firstName: z.string().min(2, 'First name must be at least 2 characters'),
  lastName: z.string().min(2, 'Last name must be at least 2 characters'),
  email: z.string().email('Valid email address is required'),
  phone: z.string().optional(),
  countryCode: z.string().optional(),
})

const notificationSettingsSchema = z.object({
  emailNotifications: z.boolean(),
  smsNotifications: z.boolean(),
  emergencyAlerts: z.boolean(),
  attendanceUpdates: z.boolean(),
  transportUpdates: z.boolean(),
  academicUpdates: z.boolean(),
})

const accountSettingsSchema = z.object({
  language: z.string(),
  timezone: z.string(),
  theme: z.string(),
  dateFormat: z.string(),
  currency: z.string(),
})

const changePasswordSchema = z.object({
  currentPassword: z.string().min(1, 'Current password is required'),
  newPassword: z.string().min(6, 'New password must be at least 6 characters'),
  confirmPassword: z.string().min(6, 'Please confirm your new password'),
}).refine(data => data.newPassword === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
})

type EditProfileData = z.infer<typeof editProfileSchema>
type ChangePasswordData = z.infer<typeof changePasswordSchema>
type NotificationSettingsData = z.infer<typeof notificationSettingsSchema>
type AccountSettingsData = z.infer<typeof accountSettingsSchema>

interface UserProfile {
  id: string
  firstName: string
  lastName: string
  fullName: string
  email: string
  phone?: string
  countryCode?: string
  role: string
  roleInfo?: {
    name: string
    description: string
  }
  institution: string
  institutionInfo?: {
    id: string
    name: string
    settings: Record<string, any>
  }
  status: string
  isActive: boolean
  emailVerified: boolean
  lastLogin?: string
  profileImage?: string
  createdBy: string
  createdAt: string
  updatedAt: string
  preferences?: {
    notifications: {
      email: boolean
      sms: boolean
      emergency: boolean
      attendance: boolean
      transport: boolean
      academic: boolean
    }
    settings: {
      language: string
      timezone: string
      theme: string
      dateFormat: string
      currency: string
    }
  }
}

const ProfilePage: React.FC = () => {
  const { user } = useAuth()
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [showPasswordFields, setShowPasswordFields] = useState(false)
  const [activeTab, setActiveTab] = useState<'profile' | 'security' | 'notifications' | 'preferences'>('profile')
  
  // Institution details
  const [institutionDetails, setInstitutionDetails] = useState<any>(null)
  const [loadingInstitution, setLoadingInstitution] = useState(false)

  const editProfileForm = useForm<EditProfileData>({
    resolver: zodResolver(editProfileSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      countryCode: 'KE',
    }
  })

  const changePasswordForm = useForm<ChangePasswordData>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    }
  })

  const notificationForm = useForm<NotificationSettingsData>({
    resolver: zodResolver(notificationSettingsSchema),
    defaultValues: {
      emailNotifications: true,
      smsNotifications: true,
      emergencyAlerts: true,
      attendanceUpdates: true,
      transportUpdates: true,
      academicUpdates: true,
    }
  })

  const accountSettingsForm = useForm<AccountSettingsData>({
    resolver: zodResolver(accountSettingsSchema),
    defaultValues: {
      language: 'en',
      timezone: 'Africa/Nairobi',
      theme: 'light',
      dateFormat: 'DD/MM/YYYY',
      currency: 'KES',
    }
  })

  useEffect(() => {
    fetchProfile()
  }, [user])

  const fetchProfile = async () => {
    if (!user?.id) return
    
    setLoading(true)
    try {
      console.log('Fetching user profile for:', user.id)
      
      // For now, use the user data from auth context
      // In a production app, you might want to fetch fresh data
      const profileData: UserProfile = {
        id: user.id,
        firstName: user.firstName,
        lastName: user.lastName,
        fullName: `${user.firstName} ${user.lastName}`,
        email: user.email,
        phone: (user as any).phone,
        countryCode: (user as any).countryCode || 'KE',
        role: user.role,
        institution: (user as any).institution || 'Not assigned',
        status: user.status || 'Active',
        isActive: true,
        emailVerified: (user as any).emailVerified || false,
        lastLogin: (user as any).lastLogin,
        profileImage: (user as any).profileImage,
        createdBy: (user as any).createdBy || 'System',
        createdAt: (user as any).createdAt || new Date().toISOString(),
        updatedAt: (user as any).updatedAt || new Date().toISOString()
      }
      
      setProfile(profileData)
      
      // Populate form with current data
      editProfileForm.reset({
        firstName: profileData.firstName,
        lastName: profileData.lastName,
        email: profileData.email,
        phone: profileData.phone || '',
        countryCode: profileData.countryCode || 'KE',
      })
      
      // Fetch institution details if user has an institution
      if (user.institutionId) {
        fetchInstitutionDetails(user.institutionId)
      }
      
    } catch (error: any) {
      console.error('Failed to fetch profile:', error)
      toast.error('Failed to load profile')
    } finally {
      setLoading(false)
    }
  }

  const fetchInstitutionDetails = async (institutionId: string) => {
    setLoadingInstitution(true)
    try {
      console.log('Fetching institution details for:', institutionId)
      
      const institution = await institutionsApi.getInstitution(institutionId)
      console.log('Institution details:', institution)
      
      setInstitutionDetails(institution)
      
    } catch (error: any) {
      console.error('Failed to fetch institution details:', error)
      // Don't show error toast for institution fetch failure
      setInstitutionDetails(null)
    } finally {
      setLoadingInstitution(false)
    }
  }

  const handleEditProfile = async (profileData: EditProfileData) => {
    try {
      console.log('Updating profile:', profileData)
      
      const response = await updateUser(user!.id, {
        firstName: profileData.firstName,
        lastName: profileData.lastName,
        email: profileData.email,
        phone: profileData.phone,
        countryCode: profileData.countryCode,
      })
      
      console.log('Profile updated:', response)
      
      toast.success('Profile updated successfully!')
      setShowEditModal(false)
      
      // Refresh profile data
      fetchProfile()
      
    } catch (error: any) {
      console.error('Failed to update profile:', error)
      toast.error(error.response?.data?.message || 'Failed to update profile')
    }
  }

  const handleChangePassword = async (_passwordData: ChangePasswordData) => {
    try {
      console.log('Changing password...')
      
      // TODO: Implement password change API
      // const response = await changePassword({
      //   currentPassword: passwordData.currentPassword,
      //   newPassword: passwordData.newPassword
      // })
      
      toast.success('Password changed successfully!')
      setShowPasswordModal(false)
      changePasswordForm.reset()
      
    } catch (error: any) {
      console.error('Failed to change password:', error)
      toast.error(error.response?.data?.message || 'Failed to change password')
    }
  }

  const handleUpdateNotifications = async (notificationData: NotificationSettingsData) => {
    try {
      console.log('Updating notification settings:', notificationData)
      
      // TODO: Implement notification settings API
      toast.success('Notification settings updated successfully!')
      
    } catch (error: any) {
      console.error('Failed to update notifications:', error)
      toast.error('Failed to update notification settings')
    }
  }

  const handleUpdateAccountSettings = async (settingsData: AccountSettingsData) => {
    try {
      console.log('Updating account settings:', settingsData)
      
      // TODO: Implement account settings API
      toast.success('Account settings updated successfully!')
      
    } catch (error: any) {
      console.error('Failed to update settings:', error)
      toast.error('Failed to update account settings')
    }
  }

  const getRoleDisplayInfo = (role: string) => {
    switch (role.toLowerCase()) {
      case 'super_admin':
        return {
          name: 'Super Administrator',
          description: 'Platform-wide management and oversight',
          color: 'bg-red-100 text-red-800',
          icon: <ShieldCheckIcon className="h-4 w-4" />
        }
      case 'school_admin':
        return {
          name: 'School Administrator',
          description: 'School-specific administration and management',
          color: 'bg-blue-100 text-blue-800',
          icon: <BuildingOfficeIcon className="h-4 w-4" />
        }
      case 'teacher':
        return {
          name: 'Teacher',
          description: 'Classroom instruction and student management',
          color: 'bg-green-100 text-green-800',
          icon: <UserCircleIcon className="h-4 w-4" />
        }
      case 'parent':
        return {
          name: 'Parent',
          description: 'Student guardian with monitoring access',
          color: 'bg-purple-100 text-purple-800',
          icon: <UserCircleIcon className="h-4 w-4" />
        }
      case 'driver':
        return {
          name: 'Driver',
          description: 'School transport and route management',
          color: 'bg-orange-100 text-orange-800',
          icon: <UserCircleIcon className="h-4 w-4" />
        }
      case 'student':
        return {
          name: 'Student',
          description: 'Student access to personal information',
          color: 'bg-indigo-100 text-indigo-800',
          icon: <UserCircleIcon className="h-4 w-4" />
        }
      default:
        return {
          name: role,
          description: 'Platform user',
          color: 'bg-gray-100 text-gray-800',
          icon: <UserCircleIcon className="h-4 w-4" />
        }
    }
  }

  const getStatusColor = (status: string, isActive: boolean) => {
    if (!isActive) return 'bg-red-100 text-red-800'
    if (status === 'Active') return 'bg-green-100 text-green-800'
    return 'bg-yellow-100 text-yellow-800'
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="dashboard-card">
          <div className="animate-pulse space-y-4">
            <div className="flex items-center space-x-4">
              <div className="w-24 h-24 bg-gray-200 rounded-full"></div>
              <div className="space-y-2">
                <div className="h-6 bg-gray-200 rounded w-48"></div>
                <div className="h-4 bg-gray-200 rounded w-32"></div>
              </div>
            </div>
            <div className="h-32 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    )
  }

  if (!profile) {
    return (
      <div className="space-y-6">
        <div className="dashboard-card">
          <div className="text-center py-12">
            <UserCircleIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">Profile not found</h3>
            <p className="mt-2 text-gray-500">Unable to load profile information</p>
          </div>
        </div>
      </div>
    )
  }

  const roleInfo = getRoleDisplayInfo(profile.role)

  return (
    <div className="space-y-6">
      {/* Profile Header */}
      <div className="dashboard-card">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between">
          <div className="flex items-center space-x-6">
            <div className="relative">
              <div className="w-24 h-24 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center">
                {profile.profileImage ? (
                  <img
                    src={profile.profileImage}
                    alt={profile.fullName}
                    className="w-full h-full rounded-full object-cover"
                  />
                ) : (
                  <UserCircleIcon className="h-12 w-12 text-white" />
                )}
              </div>
              <button className="absolute bottom-0 right-0 w-8 h-8 bg-white rounded-full shadow-lg flex items-center justify-center hover:shadow-xl transition-shadow">
                <CameraIcon className="h-4 w-4 text-gray-600" />
              </button>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{profile.fullName}</h1>
              <div className="flex items-center space-x-2 mt-1">
                {roleInfo.icon}
                <span className={`px-3 py-1 text-sm font-medium rounded-full ${roleInfo.color}`}>
                  {roleInfo.name}
                </span>
              </div>
              <p className="text-gray-600 mt-1">{roleInfo.description}</p>
              
              {/* Institution Display */}
              <div className="flex items-center space-x-2 mt-2">
                <BuildingOfficeIcon className="h-4 w-4 text-gray-500" />
                <span className="text-sm text-gray-700">
                  {profile.role.toLowerCase() === 'super_admin' 
                    ? 'Platform Administrator' 
                    : (institutionDetails?.name || profile.institution || 'No Institution Assigned')
                  }
                </span>
                {loadingInstitution && (
                  <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                )}
              </div>
              
              {/* Status */}
              <div className="flex items-center space-x-2 mt-2">
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(profile.status, profile.isActive)}`}>
                  {profile.isActive ? 'Active' : 'Inactive'}
                </span>
                {profile.emailVerified && (
                  <div className="flex items-center space-x-1">
                    <CheckCircleIcon className="h-4 w-4 text-green-600" />
                    <span className="text-xs text-green-700">Email Verified</span>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <div className="mt-6 lg:mt-0 flex space-x-3">
            <Button
              onClick={() => setShowEditModal(true)}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <PencilIcon className="h-4 w-4 mr-2" />
              Edit Profile
            </Button>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="dashboard-card">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {[
              { id: 'profile', label: 'Profile', icon: UserCircleIcon },
              { id: 'security', label: 'Security', icon: LockClosedIcon },
              { id: 'notifications', label: 'Notifications', icon: BellIcon },
              { id: 'preferences', label: 'Preferences', icon: PaletteIcon }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <tab.icon className="h-4 w-4" />
                  <span>{tab.label}</span>
                </div>
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        {activeTab === 'profile' && (
          <motion.div
            key="profile"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
            className="grid grid-cols-1 lg:grid-cols-3 gap-6"
          >
            {/* Personal Information */}
            <div className="dashboard-card">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <UserCircleIcon className="h-5 w-5 text-gray-600 mr-2" />
                Personal Information
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-600">Full Name</label>
                  <p className="text-gray-900">{profile.fullName}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Email Address</label>
                  <div className="flex items-center space-x-2">
                    <p className="text-gray-900">{profile.email}</p>
                    {profile.emailVerified && (
                      <CheckCircleIcon className="h-4 w-4 text-green-600" />
                    )}
                  </div>
                </div>
                {profile.phone && (
                  <div>
                    <label className="text-sm font-medium text-gray-600">Phone Number</label>
                    <div className="flex items-center space-x-2">
                      <span className="text-lg">{profile.countryCode === 'KE' ? '🇰🇪' : profile.countryCode === 'UG' ? '🇺🇬' : profile.countryCode === 'TZ' ? '🇹🇿' : '🌍'}</span>
                      <p className="text-gray-900">{profile.phone}</p>
                    </div>
                  </div>
                )}
                <div>
                  <label className="text-sm font-medium text-gray-600">User ID</label>
                  <p className="text-gray-900 font-mono text-sm">{profile.id}</p>
                </div>
              </div>
            </div>

        {/* Role & Institution Information */}
        <div className="dashboard-card">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <ShieldCheckIcon className="h-5 w-5 text-gray-600 mr-2" />
            Role & Institution
          </h3>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-600">Role</label>
              <div className="flex items-center space-x-2 mt-1">
                {roleInfo.icon}
                <span className={`px-3 py-1 text-sm font-medium rounded-full ${roleInfo.color}`}>
                  {roleInfo.name}
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-1">{roleInfo.description}</p>
            </div>
            
            {/* Institution Information - Different for each role */}
            {profile.role.toLowerCase() === 'super_admin' ? (
              <div>
                <label className="text-sm font-medium text-gray-600">Access Level</label>
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mt-1">
                  <div className="flex items-center space-x-2">
                    <GlobeAltIcon className="h-5 w-5 text-red-600" />
                    <span className="text-sm font-medium text-red-800">Platform Administrator</span>
                  </div>
                  <p className="text-xs text-red-700 mt-1">
                    Full access to all institutions, users, and system settings
                  </p>
                </div>
              </div>
            ) : (
              <div>
                <label className="text-sm font-medium text-gray-600">Institution</label>
                {institutionDetails ? (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-1">
                    <div className="flex items-center space-x-2">
                      <BuildingOfficeIcon className="h-5 w-5 text-blue-600" />
                      <span className="text-sm font-medium text-blue-800">{institutionDetails.name}</span>
                    </div>
                    <div className="text-xs text-blue-700 mt-1 space-y-1">
                      <p>Plan: {institutionDetails.subscriptionPlan?.charAt(0).toUpperCase() + institutionDetails.subscriptionPlan?.slice(1)}</p>
                      {institutionDetails.settings?.timezone && (
                        <p>Timezone: {institutionDetails.settings.timezone}</p>
                      )}
                      {institutionDetails.settings?.address && (
                        <p>Address: {institutionDetails.settings.address}</p>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mt-1">
                    <p className="text-sm text-gray-700">{profile.institution}</p>
                    {loadingInstitution && (
                      <p className="text-xs text-gray-500">Loading institution details...</p>
                    )}
                  </div>
                )}
              </div>
            )}

            <div>
              <label className="text-sm font-medium text-gray-600">Account Status</label>
              <div className="mt-1">
                <span className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(profile.status, profile.isActive)}`}>
                  {profile.isActive ? 'Active Account' : 'Inactive Account'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Account Activity */}
        <div className="dashboard-card">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <ClockIcon className="h-5 w-5 text-gray-600 mr-2" />
            Account Activity
          </h3>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-600">Last Login</label>
              <p className="text-gray-900 flex items-center">
                {profile.lastLogin ? (
                  <>
                    <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2" />
                    {new Date(profile.lastLogin).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })} at {new Date(profile.lastLogin).toLocaleTimeString('en-US', { 
                      hour: '2-digit', 
                      minute: '2-digit',
                      hour12: true 
                    })}
                  </>
                ) : (
                  <>
                    <ClockIcon className="h-4 w-4 text-gray-400 mr-2" />
                    <span className="text-gray-500 italic">Account created, awaiting first login</span>
                  </>
                )}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Member Since</label>
              <p className="text-gray-900 flex items-center">
                <UserCircleIcon className="h-4 w-4 text-blue-500 mr-2" />
                {new Date(profile.createdAt).toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
                <span className="ml-2 px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full">
                  {Math.ceil((new Date().getTime() - new Date(profile.createdAt).getTime()) / (1000 * 60 * 60 * 24))} days
                </span>
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Last Updated</label>
              <p className="text-gray-900 flex items-center">
                <PencilIcon className="h-4 w-4 text-orange-500 mr-2" />
                {new Date(profile.updatedAt).toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
                {profile.updatedAt !== profile.createdAt && (
                  <span className="ml-2 text-xs text-gray-500">
                    (Modified)
                  </span>
                )}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Account Created By</label>
              <p className="text-gray-900 flex items-center">
                {profile.createdBy && profile.createdBy !== 'System' ? (
                  <>
                    <UserCircleIcon className="h-4 w-4 text-purple-500 mr-2" />
                    {profile.createdBy}
                    <span className="ml-2 px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded-full">
                      Admin
                    </span>
                  </>
                ) : (
                  <>
                    <Cog6ToothIcon className="h-4 w-4 text-gray-400 mr-2" />
                    <span className="text-gray-500 italic">System Generated</span>
                    <span className="ml-2 px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full">
                      Auto-created
                    </span>
                  </>
                )}
              </p>
            </div>
          </div>
        </div>

            {/* Role-Specific Information Sections */}
            
            {/* Teacher-Specific Information */}
            {profile.role.toLowerCase() === 'teacher' && (
        <div className="dashboard-card">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <UserCircleIcon className="h-5 w-5 text-green-600 mr-2" />
            Teaching Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-green-50 rounded-lg p-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">12</div>
                <div className="text-sm text-gray-600">Active Lessons</div>
              </div>
            </div>
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">156</div>
                <div className="text-sm text-gray-600">Students Taught</div>
              </div>
            </div>
            <div className="bg-indigo-50 rounded-lg p-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-indigo-600">8</div>
                <div className="text-sm text-gray-600">Classes Assigned</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Driver-Specific Information */}
      {profile.role.toLowerCase() === 'driver' && (
        <div className="dashboard-card">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <UserCircleIcon className="h-5 w-5 text-orange-600 mr-2" />
            Transport Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-orange-50 rounded-lg p-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-orange-600">3</div>
                <div className="text-sm text-gray-600">Active Routes</div>
              </div>
            </div>
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">45</div>
                <div className="text-sm text-gray-600">Students Transported</div>
              </div>
            </div>
            <div className="bg-green-50 rounded-lg p-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">99.2%</div>
                <div className="text-sm text-gray-600">On-Time Rate</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Parent-Specific Information */}
      {profile.role.toLowerCase() === 'parent' && (
        <div className="dashboard-card">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <UserCircleIcon className="h-5 w-5 text-purple-600 mr-2" />
            Family Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-purple-50 rounded-lg p-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">2</div>
                <div className="text-sm text-gray-600">Children Enrolled</div>
              </div>
            </div>
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">98%</div>
                <div className="text-sm text-gray-600">Attendance Rate</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Admin-Specific Information */}
      {(profile.role.toLowerCase() === 'school_admin' || profile.role.toLowerCase() === 'super_admin') && (
        <div className="dashboard-card">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <ShieldCheckIcon className="h-5 w-5 text-blue-600 mr-2" />
            Administrative Overview
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {profile.role.toLowerCase() === 'super_admin' ? (
              <>
                <div className="bg-red-50 rounded-lg p-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-red-600">25</div>
                    <div className="text-sm text-gray-600">Institutions</div>
                  </div>
                </div>
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">1.2k</div>
                    <div className="text-sm text-gray-600">Total Users</div>
                  </div>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">89</div>
                    <div className="text-sm text-gray-600">RFID Terminals</div>
                  </div>
                </div>
                <div className="bg-purple-50 rounded-lg p-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-purple-600">99.8%</div>
                    <div className="text-sm text-gray-600">System Uptime</div>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">245</div>
                    <div className="text-sm text-gray-600">Students</div>
                  </div>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">28</div>
                    <div className="text-sm text-gray-600">Teachers</div>
                  </div>
                </div>
                <div className="bg-orange-50 rounded-lg p-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-orange-600">12</div>
                    <div className="text-sm text-gray-600">RFID Terminals</div>
                  </div>
                </div>
                <div className="bg-purple-50 rounded-lg p-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-purple-600">8</div>
                    <div className="text-sm text-gray-600">Active Routes</div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

            {/* Security & Preferences */}
            <div className="dashboard-card">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <KeyIcon className="h-5 w-5 text-gray-600 mr-2" />
                Quick Settings
              </h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-900">Security</h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Two-Factor Authentication</span>
                      <span className="px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded-full">
                        Not Enabled
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Password Last Changed</span>
                      <span className="text-sm text-gray-900">30 days ago</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Login Sessions</span>
                      <span className="text-sm text-blue-600">2 active</span>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-900">Preferences</h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Email Notifications</span>
                      <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                        Enabled
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Theme</span>
                      <span className="text-sm text-gray-900">Light Mode</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Language</span>
                      <span className="text-sm text-gray-900">English</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Security Tab */}
        {activeTab === 'security' && (
          <motion.div
            key="security"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
            className="space-y-6"
          >
            {/* Two-Factor Authentication */}
            <div className="dashboard-card">
              <TwoFactorSetup />
            </div>

            {/* Password Management */}
            <div className="dashboard-card">
              <h3 className="text-lg font-medium text-gray-900 mb-6 flex items-center">
                <KeyIcon className="h-5 w-5 text-gray-600 mr-2" />
                Password Management
              </h3>
              
              <div className="bg-gray-50 rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-sm font-medium text-gray-900">Account Password</span>
                    <p className="text-xs text-gray-500">Last changed 30 days ago</p>
                  </div>
                  <Button
                    onClick={() => setShowPasswordModal(true)}
                    variant="secondary"
                    size="sm"
                  >
                    <KeyIcon className="h-4 w-4 mr-2" />
                    Change Password
                  </Button>
                </div>
                
                <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <LockClosedIcon className="h-5 w-5 text-blue-600 mt-0.5" />
                    <div>
                      <h5 className="text-sm font-medium text-blue-900">Password Security</h5>
                      <p className="text-sm text-blue-700 mt-1">
                        Use a strong password with at least 8 characters, including uppercase, lowercase, numbers, and symbols.
                        Consider enabling 2FA above for enhanced security.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Active Sessions */}
            <div className="dashboard-card">
              <h3 className="text-lg font-medium text-gray-900 mb-6 flex items-center">
                <ComputerDesktopIcon className="h-5 w-5 text-gray-600 mr-2" />
                Active Sessions
              </h3>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Current Session</p>
                      <p className="text-xs text-gray-500">Chrome on macOS • Nairobi, Kenya 🇰🇪</p>
                    </div>
                  </div>
                  <span className="text-xs text-green-600 font-medium">Active now</span>
                </div>
                
                <div className="flex items-center justify-between p-4 bg-white border rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 bg-gray-400 rounded-full"></div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Mobile App</p>
                      <p className="text-xs text-gray-500">iOS SmartTag School • Last seen 2 hours ago</p>
                    </div>
                  </div>
                  <button className="text-xs text-red-600 hover:text-red-700 font-medium">
                    Revoke Access
                  </button>
                </div>
                
                <div className="flex items-center justify-between p-4 bg-white border rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 bg-gray-400 rounded-full"></div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Safari on iPad</p>
                      <p className="text-xs text-gray-500">Teacher Portal • Last seen yesterday</p>
                    </div>
                  </div>
                  <button className="text-xs text-red-600 hover:text-red-700 font-medium">
                    Revoke Access
                  </button>
                </div>
              </div>
              
              <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-start space-x-3">
                  <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mt-0.5" />
                  <div>
                    <h5 className="text-sm font-medium text-yellow-900">Session Security</h5>
                    <p className="text-sm text-yellow-700 mt-1">
                      If you see any suspicious sessions, revoke them immediately and change your password. 
                      Consider enabling 2FA for additional protection.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Notifications Tab */}
        {activeTab === 'notifications' && (
          <motion.div
            key="notifications"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            <div className="dashboard-card">
              <h3 className="text-lg font-medium text-gray-900 mb-6 flex items-center">
                <BellIcon className="h-5 w-5 text-gray-600 mr-2" />
                Notification Preferences
              </h3>
              
              <form onSubmit={notificationForm.handleSubmit(handleUpdateNotifications)}>
                <div className="space-y-6">
                  {/* Communication Notifications */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-4">Communication Channels</h4>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-sm font-medium text-gray-900">Email Notifications</span>
                          <p className="text-xs text-gray-500">Receive updates via email</p>
                        </div>
                        <input
                          {...notificationForm.register('emailNotifications')}
                          type="checkbox"
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-sm font-medium text-gray-900">SMS Notifications</span>
                          <p className="text-xs text-gray-500">Receive updates via SMS to your phone</p>
                        </div>
                        <input
                          {...notificationForm.register('smsNotifications')}
                          type="checkbox"
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Alert Types */}
                  <div className="border-t pt-6">
                    <h4 className="font-medium text-gray-900 mb-4">Alert Types</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {[
                        { key: 'emergencyAlerts', label: 'Emergency Alerts', desc: 'Critical safety notifications', color: 'text-red-600' },
                        { key: 'attendanceUpdates', label: 'Attendance Updates', desc: 'Student check-in/out notifications', color: 'text-blue-600' },
                        { key: 'transportUpdates', label: 'Transport Updates', desc: 'Bus location and schedule updates', color: 'text-green-600' },
                        { key: 'academicUpdates', label: 'Academic Updates', desc: 'Grades, assignments, and school news', color: 'text-purple-600' }
                      ].map((item) => (
                        <div key={item.key} className="bg-gray-50 rounded-lg p-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <span className={`text-sm font-medium ${item.color}`}>{item.label}</span>
                              <p className="text-xs text-gray-500">{item.desc}</p>
                            </div>
                            <input
                              {...notificationForm.register(item.key as keyof NotificationSettingsData)}
                              type="checkbox"
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex justify-end pt-4 border-t">
                    <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                      <BellIcon className="h-4 w-4 mr-2" />
                      Save Notification Settings
                    </Button>
                  </div>
                </div>
              </form>
            </div>
          </motion.div>
        )}

        {/* Preferences Tab */}
        {activeTab === 'preferences' && (
          <motion.div
            key="preferences"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            <div className="dashboard-card">
              <h3 className="text-lg font-medium text-gray-900 mb-6 flex items-center">
                <PaletteIcon className="h-5 w-5 text-gray-600 mr-2" />
                Account Preferences
              </h3>
              
              <form onSubmit={accountSettingsForm.handleSubmit(handleUpdateAccountSettings)}>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Localization Settings */}
                  <div className="space-y-6">
                    <h4 className="font-medium text-gray-900">Localization</h4>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Language</label>
                      <select
                        {...accountSettingsForm.register('language')}
                        className="form-select w-full"
                      >
                        <option value="en">English</option>
                        <option value="sw">Swahili</option>
                        <option value="fr">French</option>
                        <option value="ar">Arabic</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Timezone</label>
                      <select
                        {...accountSettingsForm.register('timezone')}
                        className="form-select w-full"
                      >
                        <option value="Africa/Nairobi">East Africa Time (UTC+3)</option>
                        <option value="Africa/Cairo">Central Africa Time (UTC+2)</option>
                        <option value="Africa/Lagos">West Africa Time (UTC+1)</option>
                        <option value="UTC">UTC</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Date Format</label>
                      <select
                        {...accountSettingsForm.register('dateFormat')}
                        className="form-select w-full"
                      >
                        <option value="DD/MM/YYYY">DD/MM/YYYY (European)</option>
                        <option value="MM/DD/YYYY">MM/DD/YYYY (US)</option>
                        <option value="YYYY-MM-DD">YYYY-MM-DD (ISO)</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Currency</label>
                      <select
                        {...accountSettingsForm.register('currency')}
                        className="form-select w-full"
                      >
                        <option value="KES">🇰🇪 Kenyan Shilling (KES)</option>
                        <option value="UGX">🇺🇬 Ugandan Shilling (UGX)</option>
                        <option value="TZS">🇹🇿 Tanzanian Shilling (TZS)</option>
                        <option value="USD">🇺🇸 US Dollar (USD)</option>
                        <option value="EUR">🇪🇺 Euro (EUR)</option>
                      </select>
                    </div>
                  </div>

                  {/* Display Settings */}
                  <div className="space-y-6">
                    <h4 className="font-medium text-gray-900">Display & Interface</h4>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Theme</label>
                      <div className="grid grid-cols-3 gap-3">
                        {[
                          { value: 'light', label: 'Light', icon: SunIcon },
                          { value: 'dark', label: 'Dark', icon: MoonIcon },
                          { value: 'system', label: 'System', icon: ComputerDesktopIcon }
                        ].map((theme) => (
                          <label key={theme.value} className="cursor-pointer">
                            <input
                              {...accountSettingsForm.register('theme')}
                              type="radio"
                              value={theme.value}
                              className="sr-only"
                            />
                            <div className={`p-4 border-2 rounded-lg text-center transition-colors ${
                              accountSettingsForm.watch('theme') === theme.value
                                ? 'border-blue-500 bg-blue-50 text-blue-700'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}>
                              <theme.icon className="h-6 w-6 mx-auto mb-2" />
                              <span className="text-sm font-medium">{theme.label}</span>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-start space-x-3">
                        <GlobeAltIcon className="h-5 w-5 text-blue-600 mt-0.5" />
                        <div>
                          <h5 className="text-sm font-medium text-blue-900">Multi-Country Support</h5>
                          <p className="text-sm text-blue-700 mt-1">
                            Your settings are automatically synced across all SmartTag School modules 
                            and support international phone numbers from {Object.keys({KE: 'Kenya', UG: 'Uganda', TZ: 'Tanzania'}).length}+ countries.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end pt-6 border-t">
                  <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                    <PaletteIcon className="h-4 w-4 mr-2" />
                    Save Preferences
                  </Button>
                </div>
              </form>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit Profile Modal */}
      <AnimatePresence>
        {showEditModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowEditModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Edit Profile</h3>
                    <p className="text-sm text-gray-500">Update your personal information</p>
                  </div>
                  <button
                    onClick={() => setShowEditModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircleIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <form onSubmit={editProfileForm.handleSubmit(handleEditProfile)} className="p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    {...editProfileForm.register('firstName')}
                    label="First Name"
                    error={editProfileForm.formState.errors.firstName?.message}
                  />

                  <Input
                    {...editProfileForm.register('lastName')}
                    label="Last Name"
                    error={editProfileForm.formState.errors.lastName?.message}
                  />

                  <Input
                    {...editProfileForm.register('email')}
                    type="email"
                    label="Email Address"
                    error={editProfileForm.formState.errors.email?.message}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number (Optional)
                  </label>
                  <PhoneNumberInput
                    value={editProfileForm.watch('phone') || ''}
                    countryCode={editProfileForm.watch('countryCode') || 'KE'}
                    onChange={(phone, countryCode) => {
                      editProfileForm.setValue('phone', phone)
                      editProfileForm.setValue('countryCode', countryCode)
                    }}
                    placeholder="Enter phone number"
                    error={editProfileForm.formState.errors.phone?.message}
                  />
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <CheckCircleIcon className="h-5 w-5 text-blue-600 mt-0.5" />
                    <div>
                      <h5 className="text-sm font-medium text-blue-900">Profile Update</h5>
                      <div className="text-sm text-blue-700 mt-1">
                        <p>Changes will be reflected immediately across the system</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <Button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    variant="secondary"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="bg-indigo-600 hover:bg-indigo-700"
                  >
                    <PencilIcon className="h-4 w-4 mr-2" />
                    Save Changes
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Change Password Modal */}
      <AnimatePresence>
        {showPasswordModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowPasswordModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Change Password</h3>
                    <p className="text-sm text-gray-500">Update your account password</p>
                  </div>
                  <button
                    onClick={() => setShowPasswordModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircleIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <form onSubmit={changePasswordForm.handleSubmit(handleChangePassword)} className="p-6 space-y-4">
                <div className="relative">
                  <Input
                    {...changePasswordForm.register('currentPassword')}
                    type={showPasswordFields ? 'text' : 'password'}
                    label="Current Password"
                    error={changePasswordForm.formState.errors.currentPassword?.message}
                  />
                </div>

                <div className="relative">
                  <Input
                    {...changePasswordForm.register('newPassword')}
                    type={showPasswordFields ? 'text' : 'password'}
                    label="New Password"
                    error={changePasswordForm.formState.errors.newPassword?.message}
                  />
                </div>

                <div className="relative">
                  <Input
                    {...changePasswordForm.register('confirmPassword')}
                    type={showPasswordFields ? 'text' : 'password'}
                    label="Confirm New Password"
                    error={changePasswordForm.formState.errors.confirmPassword?.message}
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="showPassword"
                    checked={showPasswordFields}
                    onChange={(e) => setShowPasswordFields(e.target.checked)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <label htmlFor="showPassword" className="ml-2 text-sm text-gray-700">
                    Show passwords
                  </label>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <KeyIcon className="h-5 w-5 text-yellow-600 mt-0.5" />
                    <div>
                      <h5 className="text-sm font-medium text-yellow-900">Password Security</h5>
                      <div className="text-sm text-yellow-700 mt-1">
                        <p>Use at least 8 characters with a mix of letters, numbers, and symbols</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <Button
                    type="button"
                    onClick={() => setShowPasswordModal(false)}
                    variant="secondary"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="bg-red-600 hover:bg-red-700"
                  >
                    <LockClosedIcon className="h-4 w-4 mr-2" />
                    Change Password
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default ProfilePage